import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { FiAlertCircle } from 'react-icons/fi';
import { fetchAdminTeamsList } from '../actions/leaguesActions';
import { toStoredPhone, isPhoneValid, digitsOnly } from '../utilities/phone';
import TeamPicker from './TeamPicker';
import '../styles/editModal.css';
import '../styles/leagueManagement.css';

// skipPhone — игрок уже капитан другой команды: номер у него есть, роль
// выставлена, и повторно спрашивать телефон не нужно. Тогда на сервер уходит
// только связка игрок+команда, а поле phone не отправляется совсем
// phoneRequired — сервер отклонил назначение без телефона, и мы просим
// ввести номер вручную. Полный номер API не отдаёт (только маску),
// поэтому переслать существующий нельзя
export default function AddCaptainModal({
    isOpen, onClose, onSubmit, playerName, currentPhone, takenTeamIds, skipPhone,
    phoneRequired,
}) {
    const dispatch = useDispatch();

    const [teams, setTeams] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [phone, setPhone] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedTeam(null);
        setPhone('');
        setError(null);
    }, [isOpen, currentPhone]);

    // Справочник команд клиента грузим только когда модалку открыли
    useEffect(() => {
        if (!isOpen) return undefined;
        let cancelled = false;
        setTeamsLoading(true);
        dispatch(fetchAdminTeamsList())
            .then(data => {
                if (cancelled) return;
                setTeams((Array.isArray(data) ? data : []).map(team => {
                    const name = team.name || team.team_name || 'Без названия';
                    return {
                        team_id: Number(team.team_id !== undefined ? team.team_id : team.id),
                        name,
                        image: team.image || '',
                        searchName: name.toLowerCase(),
                    };
                }));
            })
            .catch(e => {
                if (cancelled) return;
                console.error('Ошибка загрузки списка команд:', e);
                setTeams([]);
            })
            .finally(() => { if (!cancelled) setTeamsLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch, isOpen]);

    const taken = useMemo(() => new Set(takenTeamIds || []), [takenTeamIds]);

    if (!isOpen) return null;

    // Телефон нужен всем, кто назначается впервые, и тем, для кого сервер
    // отказался принять запрос без номера
    const needPhone = !skipPhone || phoneRequired;

    const submit = async () => {
        if (!selectedTeam) {
            setError('Выберите команду.');
            return;
        }
        if (needPhone && !isPhoneValid(phone)) {
            setError('Введите 10 цифр номера, без +7.');
            return;
        }
        setError(null);
        setSaving(true);
        const ok = await onSubmit(selectedTeam, needPhone ? toStoredPhone(phone) : null);
        setSaving(false);
        // Закрываем только при успехе: иначе останемся в модалке и покажем
        // поле телефона, которое попросил сервер
        if (ok) onClose();
    };

    return (
        <div className="modalOverlay" onClick={onClose}>
            <div className="modalContent modalContentWide" onClick={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <h2>Назначить капитаном</h2>
                    <button className="modalClose" onClick={onClose}>×</button>
                </div>

                <div className="modalBody modalBodyScroll">
                    <div className="seasonScopeNote">
                        <FiAlertCircle style={{ marginRight: '8px', flexShrink: 0 }} />
                        <span>
                            {skipPhone && !phoneRequired ? (
                                <>
                                    {playerName} уже капитан другой команды — номер {currentPhone}
                                    {' '}и роль у него уже есть. Осталось выбрать команду.
                                </>
                            ) : (
                                <>
                                    {playerName} станет представителем команды. Номер телефона
                                    нужен, чтобы капитан мог войти в приложение — он привязывается
                                    к игроку, а не к команде.
                                </>
                            )}
                        </span>
                    </div>

                    <div className="lmRow">
                        <label className="modalLabel">Команда</label>
                        {selectedTeam ? (
                            <div className="pickedTeam">
                                <span>{selectedTeam.name}</span>
                                <button
                                    type="button"
                                    className="linkButton"
                                    onClick={() => setSelectedTeam(null)}
                                >
                                    выбрать другую
                                </button>
                            </div>
                        ) : (
                            <TeamPicker
                                allTeams={teams}
                                loading={teamsLoading}
                                isPicked={(teamId) => taken.has(Number(teamId))}
                                onPick={(team) => { setSelectedTeam(team); setError(null); }}
                            />
                        )}
                        {taken.size > 0 && !selectedTeam && (
                            <span className="fieldHint">
                                Команды, где игрок уже представитель, отмечены галочкой
                                и повторно не выбираются.
                            </span>
                        )}
                    </div>

                    {/* Условный рендер, а не hidden: у .lmRow задан display: flex,
                        и он перебивает атрибут hidden — блок оставался виден */}
                    {needPhone ? (
                    <div className="lmRow">
                        <label className="modalLabel">Телефон капитана</label>
                        <div className="phoneRow">
                            <span className="phonePrefix">+7</span>
                            <input
                                type="tel"
                                className="modalInput"
                                inputMode="numeric"
                                maxLength={10}
                                value={phone}
                                placeholder="9991234567"
                                onChange={(e) => setPhone(digitsOnly(e.target.value).slice(0, 10))}
                            />
                        </div>
                        <span className="fieldHint">
                            10 цифр без +7.
                            {phoneRequired && currentPhone
                                ? ` Сервер не принял назначение без номера. Последние цифры записанного: ${currentPhone}.`
                                : ''}
                        </span>
                    </div>
                    ) : null}

                    {error && <span className="fieldError">{error}</span>}
                </div>

                <div className="modalFooter">
                    <button className="modalButton modalButtonCancel" onClick={onClose}>
                        Отмена
                    </button>
                    <button className="modalButton modalButtonSave" disabled={saving} onClick={submit}>
                        {saving ? 'Назначаем…' : 'Назначить капитаном'}
                    </button>
                </div>
            </div>
        </div>
    );
}
